/**
 * Inline formatting on a paragraph, as the editor holds it: **runs**, never
 * markup.
 *
 * Slice 56 let an author select words in a paragraph and make them bold,
 * italic, underlined or a different size. The contract stores that as a list of
 * runs on the text block — `{ text, bold?, italic?, underline?, size? }` — and
 * keeps the block's plain `text` equal to the runs joined, so every consumer of
 * `text` (the plain-text alternative, the variable scan, the save-time variable
 * refusal, search, the list's quotations) goes on working without knowing that
 * formatting exists. This file is the editor's half of that: the algebra of
 * "make characters 12 to 30 bold" over runs, with no DOM anywhere in it.
 *
 * ## Why runs, and why the algebra is here rather than in the browser
 *
 * **Rejected: `document.execCommand('bold')` and storing `innerHTML`.** The
 * browser's own formatting commands are what every tutorial reaches for, and
 * they are the reason this file exists. They write whatever markup the browser
 * prefers that day — `<b>`, `<strong>`, `<span style="font-weight: 700">`,
 * nested and un-nested differently in Chrome and Firefox — and a stored
 * document built from that is a stored document of browser opinions, which then
 * has to be sanitised on the way in and trusted on the way out. The owner
 * explicitly stopped a raw-HTML block for the same reason. So a mark is applied
 * **here**, to the runs, by offsets into the plain text; the DOM is redrawn from
 * the result (`text-runs-dom.ts`), and it is only ever read back through a
 * whitelist walker. The DOM is a view of the runs. It is never the data.
 *
 * **Rejected: a rich-text library (Lexical, ProseMirror, Tiptap, Slate).** Each
 * is the right answer for a document editor and the wrong one for four marks on
 * one paragraph: tens of kilobytes, a schema of its own to keep in step with the
 * contract's, and a serialiser between its model and ours that would be a
 * second place for "what is a run" to be decided. Four marks over a flat list
 * of runs is small enough to own outright.
 *
 * ## One normaliser, not two
 *
 * Canonical marks, empty runs dropped, adjacent runs with identical marks
 * merged. That rule is the contract's — `normaliseTextRuns` in `@wend/shared`,
 * which the API runs on every parse — and every function below that returns
 * runs returns them through it. A second copy here would be the one that
 * drifts, and the way it would drift is a document the editor thinks is clean
 * and the server rewrites on save, which shows up as "unsaved changes" that
 * never go away.
 *
 * **One step of it is held back while typing, and only one.** The shared
 * normaliser also trims the paragraph's leading and trailing whitespace,
 * because the paragraph's `text` is trimmed when the contract parses it and
 * the two must stay equal. Applied on every keystroke, that trim eats the space
 * a person has just typed after a word — "Hello " becomes "Hello", and the next
 * word lands against it — and the newline they have just typed at the end.
 * A `<textarea>` never had this problem, because the draft holds what was
 * typed and only the parse trims. So {@link normaliseRuns} pads the runs with
 * one non-space character at each end, in the marks of the run it touches,
 * calls the shared normaliser — which then has nothing to trim and merges the
 * pads into their neighbours — and takes the pads off again. Every other step
 * is the shared function's own. The trim still happens, where it always did:
 * when the preview or the save parses the draft.
 *
 * ## A variable is never split — the mark is WIDENED, not refused
 *
 * Substitution runs on the compiled HTML. `{{ name }}` with its first half bold
 * compiles to `<strong>{{ na</strong>me }}`, which no substituter will ever
 * match, and the recipient receives the braces. The contract refuses such a
 * block at save; the editor must never produce one. Two ways to hold that:
 * refuse a selection that cuts a token, or widen it to cover the whole token.
 * **This editor widens** ({@link wholeVariableRange}): a person who drags across
 * "Dear {{ na" and presses Bold means the greeting, and a refusal would read as
 * a broken button. Typing can also split one — type `{{ name` inside bold text,
 * leave it, type ` }}` — so after every edit {@link keepVariablesWhole} gives
 * each token the marks of its first character.
 *
 * ## Size 16 is not stored
 *
 * The contract says an absent `size` is the body default, 16px. Choosing 16
 * therefore *removes* the size rather than writing `size: 16`, so "set it to 18
 * and back to 16" returns the exact runs it started from rather than a run that
 * compiles to a redundant `<span>`.
 *
 * ## A block with no formatting carries no runs
 *
 * {@link withRuns} drops `runs` entirely when nothing in the paragraph is
 * formatted. A block without runs is exactly the block every template had
 * before this slice, and it compiles through the renderer's old path byte for
 * byte — which is what keeps a template nobody formats identical on the wire.
 * Bold-then-unbold therefore round-trips to the original block, not to a block
 * with one plain run.
 */
import {
  DEFAULT_TEXT_RUN_SIZE,
  TEXT_RUN_SIZE_VALUES,
  normaliseTextRuns,
  templateVariablePattern,
  textRunsCarryFormatting,
  variablesSplitByRuns,
  type SplitTemplateVariable,
  type TextBlock,
  type TextRun,
  type TextRunSize,
} from '@wend/shared';

/* -------------------------------------------------------------------------- */
/* Types and sizes, from the contract                                          */
/* -------------------------------------------------------------------------- */

export type { TextRun, TextRunSize };
export type TextRunFlag = 'bold' | 'italic' | 'underline';

export const TEXT_RUN_FLAGS: readonly TextRunFlag[] = ['bold', 'italic', 'underline'];

/** The body default. Absent `size` means this; it is never written. */
export const BODY_TEXT_SIZE: TextRunSize = DEFAULT_TEXT_RUN_SIZE;

/** The sizes the picker offers — the contract's list, smallest first. */
export const TEXT_RUN_SIZES: readonly TextRunSize[] = TEXT_RUN_SIZE_VALUES;

/**
 * The size picker's labels — one per size the contract allows.
 *
 * A `Record` over the contract's own union, so a size added to the contract is
 * a compile error here until it has a label. The default says so, because the
 * default is the one size that is stored as nothing.
 */
export const TEXT_RUN_SIZE_LABEL: Readonly<Record<TextRunSize, string>> = {
  14: '14',
  16: '16 · default',
  18: '18',
  20: '20',
  24: '24',
  28: '28',
  32: '32',
};

export function isTextRunSize(value: number): value is TextRunSize {
  return TEXT_RUN_SIZES.some((size) => size === value);
}

/* -------------------------------------------------------------------------- */
/* Marks                                                                       */
/* -------------------------------------------------------------------------- */

/** A run's formatting with every flag spelled out — never stored in this shape. */
export interface RunMarks {
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underline: boolean;
  /** Null is the body default. */
  readonly size: TextRunSize | null;
}

export const PLAIN_MARKS: RunMarks = { bold: false, italic: false, underline: false, size: null };

export function marksOf(run: TextRun): RunMarks {
  return {
    bold: run.bold === true,
    italic: run.italic === true,
    underline: run.underline === true,
    size: run.size === undefined || run.size === BODY_TEXT_SIZE ? null : run.size,
  };
}

/**
 * A run in the contract's shape: flags **present only when true**, size absent
 * at the default. The contract's run schema is `.strict()` and its flags are
 * `true` literals, so `bold: false` is not a spelling of "not bold" — it is a
 * document the API refuses. Every run this file makes goes through here.
 */
export function runOf(text: string, marks: RunMarks): TextRun {
  return {
    text,
    ...(marks.bold ? { bold: true as const } : {}),
    ...(marks.italic ? { italic: true as const } : {}),
    ...(marks.underline ? { underline: true as const } : {}),
    ...(marks.size === null ? {} : { size: marks.size }),
  };
}


/* -------------------------------------------------------------------------- */
/* Blocks ⇄ runs                                                               */
/* -------------------------------------------------------------------------- */

export function joinRuns(runs: readonly TextRun[]): string {
  return runs.map((run) => run.text).join('');
}

/** A character that is never whitespace, so the shared trim has nothing to cut. */
const PAD = 'x';

/**
 * The contract's normaliser, with its trim held back until the parse — see
 * "One normaliser, not two" in the header for why, and for why this is not a
 * second normaliser: everything but the trim is `normaliseTextRuns` itself.
 */
export function normaliseRuns(runs: readonly TextRun[]): TextRun[] {
  const first = runs.find((run) => run.text !== '');
  const last = [...runs].reverse().find((run) => run.text !== '');
  if (first === undefined || last === undefined) return [];

  const padded = normaliseTextRuns([
    runOf(PAD, marksOf(first)),
    ...runs,
    runOf(PAD, marksOf(last)),
  ]);

  // The pads merged into the first and last runs, whose marks they carry.
  const out = padded.map((run) => ({ ...run }));
  const head = out[0];
  if (head !== undefined) head.text = head.text.slice(PAD.length);
  const tail = out[out.length - 1];
  if (tail !== undefined) tail.text = tail.text.slice(0, tail.text.length - PAD.length);
  return out.filter((run) => run.text !== '');
}

/**
 * Line breaks as the surface counts them: CRLF and a lone CR as LF, across run
 * boundaries too — a `\r` ending one run and the `\n` opening the next are one
 * break. The contract refuses that boundary at save, so it is not expected; if
 * it arrives anyway it becomes one line break, not two.
 */
function lineFeedsOnly(runs: readonly TextRun[]): TextRun[] {
  let afterCr = false;
  return runs.map((run) => {
    const text = afterCr && run.text.startsWith('\n') ? run.text.slice(1) : run.text;
    afterCr = run.text === '' ? afterCr : run.text.endsWith('\r');
    return { ...run, text: text.replace(/\r\n?/g, '\n') };
  });
}

/**
 * A block's runs — its own, or its whole text as one plain run — with CRLF read
 * as LF.
 *
 * The surface is read through a walker that counts `\r\n` as one character, so
 * the runs the editor holds must count it the same way, or every offset after a
 * line break is off by one per break: a mark lands a character early, and can
 * land between `\r` and `\n`, which the contract refuses. A stored paragraph
 * holding CRLF therefore draws as LF, and the first edit saves it that way.
 */
export function runsOfBlock(block: TextBlock): TextRun[] {
  if (block.runs !== undefined) return normaliseRuns(lineFeedsOnly(block.runs));
  return block.text === '' ? [] : [{ text: block.text.replace(/\r\n?/g, '\n') }];
}

/**
 * The block with these runs, `text` recomputed from them.
 *
 * `text` is never taken from anywhere else: the contract refuses a block whose
 * `text` is not its runs joined, and deriving one from the other in one place is
 * how the editor cannot produce that block. `runs` is dropped when nothing is
 * formatted — see the header.
 */
export function withRuns(block: TextBlock, runs: readonly TextRun[]): TextBlock {
  const normal = keepVariablesWhole(runs);
  const text = joinRuns(normal);
  // Destructured out so a paragraph with no formatting has no `runs` key at
  // all, rather than `runs: undefined` — which JSON drops but a signature over
  // the draft would not.
  const { runs: _previous, ...rest } = block;
  return textRunsCarryFormatting(normal) ? { ...rest, text, runs: normal } : { ...rest, text };
}

/* -------------------------------------------------------------------------- */
/* Ranges                                                                      */
/* -------------------------------------------------------------------------- */

/** A boundary at `offset`, splitting the run that straddles it. */
function splitAt(runs: readonly TextRun[], offset: number): TextRun[] {
  const out: TextRun[] = [];
  let position = 0;
  for (const run of runs) {
    const end = position + run.text.length;
    if (offset > position && offset < end) {
      const cut = offset - position;
      out.push({ ...run, text: run.text.slice(0, cut) }, { ...run, text: run.text.slice(cut) });
    } else {
      out.push(run);
    }
    position = end;
  }
  return out;
}

/** `change` applied to exactly the characters in [start, end). */
function mapRange(
  runs: readonly TextRun[],
  start: number,
  end: number,
  change: (run: TextRun) => TextRun,
): TextRun[] {
  let position = 0;
  const changed = splitAt(splitAt(runs, start), end).map((run) => {
    const from = position;
    position += run.text.length;
    return run.text.length > 0 && from >= start && position <= end ? change(run) : run;
  });
  return normaliseRuns(changed);
}

/** The runs covering [start, end), cut to it. */
function runsWithin(runs: readonly TextRun[], start: number, end: number): TextRun[] {
  let position = 0;
  return splitAt(splitAt(runs, start), end).filter((run) => {
    const from = position;
    position += run.text.length;
    return run.text.length > 0 && from >= start && position <= end;
  });
}

/** The marks of the character at `index`, or null past the end. */
export function marksAt(runs: readonly TextRun[], index: number): RunMarks | null {
  let position = 0;
  for (const run of runs) {
    const end = position + run.text.length;
    if (index >= position && index < end) return marksOf(run);
    position = end;
  }
  return null;
}

/**
 * The range widened to cover every `{{ … }}` token it cuts into.
 *
 * The widening rule of the header. A collapsed range is returned as it is —
 * a caret cuts nothing. The token grammar is the contract's own
 * (`templateVariablePattern`), so "what is a variable" has one answer.
 */
export function wholeVariableRange(
  text: string,
  start: number,
  end: number,
): { start: number; end: number } {
  if (start >= end) return { start, end };
  let from = start;
  let to = end;
  for (const match of text.matchAll(templateVariablePattern())) {
    const tokenStart = match.index ?? 0;
    const tokenEnd = tokenStart + match[0].length;
    if (tokenStart < to && tokenEnd > from) {
      from = Math.min(from, tokenStart);
      to = Math.max(to, tokenEnd);
    }
  }
  return { start: from, end: to };
}

/**
 * Every token in the runs that a run boundary falls inside of.
 *
 * The contract's own rule, called rather than restated. This file used to
 * carry its own copy of "a boundary strictly inside a {{ … }} token", and a
 * second copy of the rule that decides whether a variable reaches a recipient
 * is exactly how the editor and the save-time refusal come to disagree about
 * one paragraph. The name stays so the editor's callers do not move.
 */
export function splitVariables(runs: readonly TextRun[]): SplitTemplateVariable[] {
  return variablesSplitByRuns(runs);
}

/**
 * Normalised runs in which no token is cut: each cut token takes the marks of
 * its own first character. The typing half of the header's rule.
 */
export function keepVariablesWhole(runs: readonly TextRun[]): TextRun[] {
  let current = normaliseRuns(runs);
  for (const { start, end } of splitVariables(current)) {
    const marks = marksAt(current, start) ?? PLAIN_MARKS;
    current = mapRange(current, start, end, (run) => runOf(run.text, marks));
  }
  return current;
}

/* -------------------------------------------------------------------------- */
/* Edits                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Bold, italic or underline over [start, end) — on if any character in the
 * range lacks it, off if every one has it. The rule every word processor uses,
 * and the one that makes a second press undo the first.
 */
export function toggleFlag(
  runs: readonly TextRun[],
  start: number,
  end: number,
  flag: TextRunFlag,
): TextRun[] {
  const within = runsWithin(runs, start, end);
  if (within.length === 0) return normaliseRuns(runs);
  const on = !within.every((run) => marksOf(run)[flag]);
  return mapRange(runs, start, end, (run) => runOf(run.text, { ...marksOf(run), [flag]: on }));
}

/** A size over [start, end). Null — or 16 — is the body default, stored as nothing. */
export function setSize(
  runs: readonly TextRun[],
  start: number,
  end: number,
  size: TextRunSize | null,
): TextRun[] {
  const stored = size === BODY_TEXT_SIZE ? null : size;
  return mapRange(runs, start, end, (run) => runOf(run.text, { ...marksOf(run), size: stored }));
}

/**
 * `text` in place of [start, end), carrying the marks of the character before
 * the caret — so typing at the end of a bold word goes on being bold, as it
 * does everywhere else. At the very start it takes the first character's.
 */
export function insertText(
  runs: readonly TextRun[],
  start: number,
  end: number,
  text: string,
): TextRun[] {
  const marks = (start > 0 ? marksAt(runs, start - 1) : marksAt(runs, 0)) ?? PLAIN_MARKS;
  const out: TextRun[] = [];
  let position = 0;
  let inserted = false;
  for (const run of splitAt(splitAt(runs, start), end)) {
    const from = position;
    position += run.text.length;
    if (!inserted && from >= start) {
      out.push(runOf(text, marks));
      inserted = true;
    }
    if (from >= start && position <= end) continue;
    out.push(run);
  }
  if (!inserted) out.push(runOf(text, marks));
  return keepVariablesWhole(out);
}

/* -------------------------------------------------------------------------- */
/* What the toolbar shows                                                      */
/* -------------------------------------------------------------------------- */

export interface SelectionMarks {
  /** True when every selected character has the flag. */
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underline: boolean;
  /** The one size of every selected character, or `'mixed'`. Null is the default. */
  readonly size: TextRunSize | null | 'mixed';
}

export function selectionMarks(runs: readonly TextRun[], start: number, end: number): SelectionMarks {
  const within = runsWithin(runs, start, end).map(marksOf);
  if (within.length === 0) return { bold: false, italic: false, underline: false, size: null };
  const sizes = new Set(within.map((marks) => marks.size));
  const [only] = [...sizes];
  return {
    bold: within.every((marks) => marks.bold),
    italic: within.every((marks) => marks.italic),
    underline: within.every((marks) => marks.underline),
    size: sizes.size === 1 && only !== undefined ? only : 'mixed',
  };
}

/** Runs compared as data — the editor's "did anything actually change". */
export function sameRuns(a: readonly TextRun[], b: readonly TextRun[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((run, index) => {
    const other = b[index];
    if (other === undefined || other.text !== run.text) return false;
    const x = marksOf(run);
    const y = marksOf(other);
    return x.bold === y.bold && x.italic === y.italic && x.underline === y.underline && x.size === y.size;
  });
}
